import { html, render } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { map } from "lit/directives/map.js";
import { AppComponent } from "../component";
import { msg } from "@omegagrid/localize";
import { SqlDataset, SqlExportFormat, SqlMessage } from "./sqlEditorAdapter";
import { actions, ComponentStore, dom, DropdownMenu, MenuEvent, SplitContainer, SplitContainerItem } from "@omegagrid/core";
import { resultStyle } from "./sqlEditor.style";
import { TabEvent, TabItem, Tabs } from "@omegagrid/tabs";
import { CellModel, Grid, GridSource, Renderer } from "@omegagrid/grid";
import { Code } from "@omegagrid/code";
import rp from "../global";
import type { SettingsChangeEvent } from "../container/settings";
import { getEditorSettings, RESULT_TYPES_SETTING } from "./editorSettings";
import { createSqlResultColumns } from "./sqlResultColumns";


export type SqlEditorResultViewType = 'grid'|'text';
export type SqlEditorResultLayoutType = 'column'|'row'|'stack';

const ROW_HEIGHT = 18;

const afterRenderer: Renderer = (div: HTMLDivElement, cell: CellModel) => {
	if (cell.value == null) {
		div.style.textAlign = 'left';
		div.style.fontStyle = 'italic';
		div.style.opacity = '0.5';
		div.innerText = 'NULL';
	} else if (cell.type == 't') {
		if (cell.value && (cell.value as string)[0] == '!') {
			if (((cell.value as string)[1] || '').toUpperCase() == 'H') {
				div.innerHTML = (cell.value as string).slice(2);
			}
		}
	}
	return div;
};

function convertDatasetToGridData(dataset: SqlDataset): GridSource {
	return {
		// the header tooltips with the declared types follow the setting (gear menu of the editor)
		columns: createSqlResultColumns(dataset.columns, {types: getEditorSettings().resultTypes}),
		rows: dataset.data.map(row => ({data: row})),
		options: {
			columnSorting: true,
			filters: {enabled: true},
			colLetters: false,
			fontSize: 11,
			rowHeight: ROW_HEIGHT,
			numberFormat: '0.000',
			autofill: false,
			formulas: false,
			afterRenderer: afterRenderer
		}
	};
}

function createDatasetGridComponent(dataset: SqlDataset): Grid {
	const grid = dom.createElement<Grid>('og-grid');
	grid.setSourceData(convertDatasetToGridData(dataset));
	grid.style.width = '100%';
	grid.style.height = '100%';
	return grid;
}

function createDatasetTextComponent(dataset: SqlDataset) {
	const code = dom.createElement<Code>('og-code');
	code.language = 'text/plain';
	code.content = dataset.columns.map(column => column.name).join('\t') + '\n' +
		dataset.data.map(row => row.map(v => (v ?? '').toString()).join('\t')).join('\n');
	return code;
}

/** PRINT output has code 0; anything else in the messages is a real warning */
function hasWarning(message: SqlMessage): boolean {
	return message.messages?.some(m => m.code > 0) ?? false;
}

function createMessageComponent(message: SqlMessage) {
	if (!message.errors && !message.messages && !message.content) return null;

	const component = dom.createElement<HTMLElement>('div');
	if (message.content) {
		component.style.padding = '10px';
		component.innerHTML = message.content;
	} else if (message.messages) {
		// pure PRINT output is information, not a problem; the text keeps its line breaks like in SSMS
		render(html`
			<og-alert type="${hasWarning(message) ? 'warning' : 'info'}">
				<div slot="content">
					${map(message.messages, m => html`<div style="white-space: pre-wrap">${m.line > 0 ? html`<i>Line ${m.line.toString()}</i>: ` : ``}${m.message}</div>`)}
				</div>
			</og-alert>
		`, component);
	} else {
		render(html`
			<og-alert type="danger">
				<div slot="content">
					${map(message.errors, err => html`
						<div>
							${err.line > 0 ? html`<i>Line ${err.line.toString()}</i>: ` : ``}${err.error}
						</div>
					`)}
				</div>
			</og-alert>
		`, component);
	}

	return component;
}

@customElement('rp-editor-sql-result')
export class SqlEditorResult extends AppComponent {

	static styles = [resultStyle];

	@property({type: Array})
	items: [SqlDataset|SqlMessage, 'dataset'|'message'][] = [];

	@property({type: String})
	layoutType: SqlEditorResultLayoutType = 'stack';

	@property({type: String})
	viewType: SqlEditorResultViewType = 'grid';

	@property({type: Number})
	activeIndex: number = -1;

	@property({attribute: false})
	onExport: (format: SqlExportFormat, datasetCount: number) => void = null;

	@query('og-tabs')
	tabs: Tabs;

	@query('og-splitcontainer')
	splitContainer: SplitContainer;

	@query('.component-container')
	componentContainer: HTMLDivElement;

	componentStore = new ComponentStore((index: number) => {
		if (this.items[index][1] == 'message') {
			return createMessageComponent(this.items[index][0] as SqlMessage);
		} else if (this.viewType == 'grid') {
			return createDatasetGridComponent(this.items[index][0] as SqlDataset);
		} else {
			return createDatasetTextComponent(this.items[index][0] as SqlDataset);
		}
	});

	clear() {
		this.componentStore.clear();
		this.activeIndex = -1;
		this.items = [];
	}

	async renderComponent() {
		await this.updateComplete;
		if (this.componentContainer && this.isConnected) {
			dom.empty(this.componentContainer);
			const component = this.activeIndex > -1 ? await this.componentStore.get(this.activeIndex) : null;

			setTimeout(() => {
				if (component) {
					dom.empty(this.componentContainer);
					this.componentContainer.appendChild(component);
				} else {
					dom.empty(this.componentContainer);
				}
			}, 1);
		}
	}

	@query('.layout-menu')
	menu: DropdownMenu;

	updated() {
		this.renderComponent();
		if ((this.menu as any)._menu) {
			//TODO: remove when fixed in og
			this.menu.menu.items = this.menu.items;
			this.menu.menu.list.renderItems(true);
		}
	}

	/** the value of the column types setting the grids were built with */
	private _resultTypes = getEditorSettings().resultTypes;

	/**
	 * The column type tooltips follow the setting: when it changed, the grids are built again (as on a change of
	 * the view type). Checked on every change event and when the component connects, because the host detaches
	 * the component with its tab and a change made from the editor's gear menu meanwhile is not delivered.
	 */
	private _applyResultTypes() {
		const resultTypes = getEditorSettings().resultTypes;
		if (resultTypes === this._resultTypes) return;
		this._resultTypes = resultTypes;
		if (this.viewType != 'grid') return;   // the text view has no grids; a switch to the grid view rebuilds anyway
		this.componentStore.clear();
		this.requestUpdate();
	}

	private _onSettingsChange = (e: SettingsChangeEvent) => {
		if (e.detail.id === RESULT_TYPES_SETTING) this._applyResultTypes();
	};

	connectedCallback(): void {
		super.connectedCallback();
		rp.settings.addEventListener('change', this._onSettingsChange);
		this._applyResultTypes();
		if (this.activeIndex < 0 && this.items.length > 0) this.activeIndex = 0;
		this.requestUpdate();
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		rp.settings.removeEventListener('change', this._onSettingsChange);
	}

	addDataset(dataset: SqlDataset) {
		this.items = [...this.items, [dataset, 'dataset']];
		if (this.activeIndex < 0) this.activeIndex = 0;
	}

	addMessage(message: SqlMessage) {
		this.items = [...this.items, [message, 'message']];
		if (this.activeIndex < 0) this.activeIndex = 0;
	}

	get hasDatasets(): boolean {
		return this.items?.some(([, kind]) => kind === 'dataset') ?? false;
	}

	private _export(format: SqlExportFormat) {
		const datasetCount = this.items.filter(([, kind]) => kind === 'dataset').length;
		if (!datasetCount) return;                         // no dataset to export
		this.onExport?.(format, datasetCount);
	}

	_onToolbarAction(e: actions.ActionEvent) {
		switch (e.action.key) {
			case 'layoutType': this.layoutType = e.action.value as SqlEditorResultLayoutType; break;
			case 'viewType': this.viewType = e.action.value as SqlEditorResultViewType; break;
		}
	}

	willUpdate(props: Map<string, unknown>) {
		if (props.has('viewType')) {
			this.componentStore.clear();
		}
	}

	layout() {
		this.splitContainer?.layout();
	}

	get tabItems(): TabItem[] {
		if (!this.items || this.items.length === 0) return [];
		if (this.layoutType == 'stack') {
			const descriptionCounters = new Map<string, number>();
			const tabs: TabItem[] = [];

			for (let i = 0; i < this.items.length; i++) {
				const item = this.items[i];
				const isDataset = item[1] == 'dataset';
				const data = item[0] as SqlDataset | SqlMessage;

				let index: number;
				if (data.description) {
					const currentCount = descriptionCounters.get(data.description) || 0;
					index = currentCount + 1;
					descriptionCounters.set(data.description, index);
				} else {
					index = i + 1;
				}

				let title: string, color: string;
				if (isDataset) {
					title = `Table #${index}`;
				} else if ((data as SqlMessage).messages) {
					const warning = hasWarning(data as SqlMessage);
					title = warning ? `Warning #${index}` : `Output #${index}`;
					color = warning ? 'ffa500' : undefined;
				} else {
					title = `Message #${index}`;
					color = (data as SqlMessage).errors?.length > 0 ? 'ff0000' : undefined;
				}
				title = data.description ? `${data.description}: ${title}` : title;
				if (isDataset) title += ` (${(data as SqlDataset).data?.length})`;

				tabs.push({id: i, title, color});
			}

			return tabs;
		} else {
			return [{id: -1, title: 'Results'}];
		}
	}

	get containerItems(): SplitContainerItem[] {
		if (!this.items || this.items.length === 0) {
			return [];
		}
		return this.items.map((_item, i) => ({id: i}));
	}

	get layoutMenuItems() {
		return [
			{k: 'stack', v: 'Stack', icon: this.layoutType == 'stack' ? 'check' : ''},
			{k: 'column', v: 'Column', icon: this.layoutType == 'column' ? 'check' : ''},
			{k: 'row', v: 'Row', icon: this.layoutType == 'row' ? 'check' : '', divider: true},
			{k: 'grid', v: 'Grid', icon: this.viewType == 'grid' ? 'check' : ''},
			{k: 'text', v: 'Text', icon: this.viewType == 'text' ? 'check' : ''},
		];
	}

	render = () => html`
		<div class="head">
			${this.items && this.items.length > 0 ? html`
				<og-tabs
					scrollable
					.items="${this.tabItems}"
					.selectedIndex="${this.activeIndex}"
					@tab.select="${(e: TabEvent) => this.activeIndex = e.index}">
				</og-tabs>
			` : ''}

			<div class="controls">
				${this.hasDatasets && this.onExport ? html`
					<og-dropdown-menu
						dynamicWidth
						noCaret
						icon="file-arrow-down"
						color="transparent"
						@menu.select="${(e: MenuEvent) => this._export(e.item.k as SqlExportFormat)}"
						.items="${[
							{k: 'xlsx', v: msg('Export to {t}', { data: { t: 'XLSX' } }), icon: 'file-excel'},
							{k: 'csv', v: msg('Export to {t}', { data: { t: 'CSV' } }), icon: 'file-csv'},
						]}">
					</og-dropdown-menu>
				` : ''}

				<og-dropdown-menu
					class="layout-menu"
					dynamicWidth
					noCaret
					icon="gear"
					color="transparent"
					@menu.select="${(e: MenuEvent) => {
						if (['grid', 'text'].includes(e.item.k as string)) {
							this.viewType = e.item.k as SqlEditorResultViewType
						} else {
							this.layoutType = e.item.k as SqlEditorResultLayoutType
						}
					}}"
					.items="${this.layoutMenuItems}">
				</og-dropdown-menu>
			</div>
		</div>

		<div class="body">
			${this.layoutType == 'stack' ? html`
				<div class="component-container"></div>
			` : html`
				<og-splitcontainer
					.items="${this.containerItems}"
					.createComponent="${(id: number) => this.componentStore.get(id)}"
					.orientation="${this.layoutType == 'column' ? 'vertical' : 'horizontal'}">
				</og-splitcontainer>
			`}
		</div>
	`;

}
